import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as businessPlanApi from "@/api/endpoints/business/business-plan"
import { businessKeys } from "@/queries/business/business"
import { resolveEnabled, type QueryOptions } from "@/queries/query-helpers"

export const businessPlanKeys = {
  all: ["business-plans"] as const,
  list: () => [...businessPlanKeys.all, "list"] as const,
  detail: (id: string) => [...businessPlanKeys.all, "detail", id] as const,
  subscription: (businessId: string) =>
    [...businessPlanKeys.all, "subscription", businessId] as const,
}

export const useGetBusinessPlansQuery = (options?: QueryOptions) =>
  useQuery({
    queryKey: businessPlanKeys.list(),
    queryFn: () => businessPlanApi.getBusinessPlans(),
    enabled: resolveEnabled(options),
  })

export const useGetBusinessPlanByIdQuery = (
  planId: string,
  options?: QueryOptions
) =>
  useQuery({
    queryKey: businessPlanKeys.detail(planId),
    queryFn: () => businessPlanApi.getBusinessPlanById(planId),
    enabled: resolveEnabled(options) && !!planId,
  })

export const useGetBusinessSubscriptionQuery = (
  businessId: string,
  options?: QueryOptions
) =>
  useQuery({
    queryKey: businessPlanKeys.subscription(businessId),
    queryFn: () => businessPlanApi.getBusinessSubscription(businessId),
    enabled: resolveEnabled(options) && !!businessId,
  })

export const usePayBusinessSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (businessId: string) => businessPlanApi.payBusinessSubscription(businessId),
    onSuccess: (_data, businessId) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.detail(businessId) })
      queryClient.invalidateQueries({
        queryKey: businessPlanKeys.subscription(businessId),
      })
    },
  })
}

export const useUpgradeBusinessSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      businessId,
      businessPlanId,
    }: {
      businessId: string
      businessPlanId: string
    }) => businessPlanApi.upgradeBusinessSubscription(businessId, businessPlanId),
    onSuccess: (_data, { businessId }) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.detail(businessId) })
      queryClient.invalidateQueries({
        queryKey: businessPlanKeys.subscription(businessId),
      })
    },
  })
}
